import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./GDPRConsent.css";

interface GDPRConsentProps {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  error?: string;
}

export const GDPRConsent: React.FC<GDPRConsentProps> = ({
  id,
  checked,
  onChange,
  error,
}) => {
  const { t } = useTranslation();
  const [hasScrolled, setHasScrolled] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      const scrolledToBottom = scrollTop + clientHeight >= scrollHeight - 10;
      setHasScrolled(scrolledToBottom || scrollTop > 0);
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, []);

  return (
    <div className="gdpr-consent-container">
      <div
        ref={scrollContainerRef}
        className="gdpr-text-container"
        onScroll={handleScroll}
        role="region"
        aria-label={t("gdpr.title")}
        tabIndex={0}
      >
        <p className="gdpr-text">{t("gdpr.text")}</p>
      </div>
      {!hasScrolled && (
        <div className="gdpr-scroll-indicator" aria-hidden="true">
          {t("gdpr.scrollIndicator")}
        </div>
      )}
      <div className="gdpr-checkbox-wrapper">
        <input
          type="checkbox"
          id={id}
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="gdpr-checkbox"
          required
          aria-invalid={error ? "true" : "false"}
          aria-describedby={error ? `${id}-error` : undefined}
        />
        <label htmlFor={id} className="gdpr-checkbox-label">
          {t("gdpr.consentLabel")}
          <span className="required-indicator" aria-label="required">*</span>
        </label>
      </div>
      {error && (
        <div id={`${id}-error`} className="gdpr-error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
};

